import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { AlertCircle, Plus, Trash2, ChevronRight } from 'lucide-react';
import api from '../../utils/api';
import { useTenant } from '../../context/TenantContext';

const PAISES = [
  { code: 'ES', name: 'España' },
  { code: 'PT', name: 'Portugal' },
  { code: 'FR', name: 'Francia' },
  { code: 'IT', name: 'Italia' },
  { code: 'DE', name: 'Alemania' },
  { code: 'GB', name: 'Reino Unido' },
  { code: 'NL', name: 'Países Bajos' },
  { code: 'BE', name: 'Bélgica' },
];

const EU_COUNTRIES = ['ES', 'PT', 'FR', 'IT', 'DE', 'NL', 'BE', 'AT', 'IE', 'GR', 'PL'];

const TIPOS_IVA = [
  { value: '21', label: '21% (General)' },
  { value: '10', label: '10% (Reducido)' },
  { value: '4', label: '4% (Superreducido)' },
  { value: '0', label: '0% (Exento)' },
];

const METODOS_PAGO = [
  { value: 'transferencia', label: 'Transferencia bancaria' },
  { value: 'tarjeta', label: 'Tarjeta de crédito' },
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'pagare', label: 'Pagaré' },
];

export default function EmitirFacturaForm({ initialData, onSubmit }) {
  const { activeTenant } = useTenant();
  const [catalogClients, setCatalogClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [formData, setFormData] = useState({
    cliente_nombre: initialData?.cliente_nombre || '',
    cliente_nif: initialData?.cliente_nif || '',
    cliente_email: initialData?.cliente_email || '',
    cliente_direccion: initialData?.cliente_direccion || '',
    cliente_pais: initialData?.cliente_pais || 'ES',
    operacion_intracomunitaria: initialData?.operacion_intracomunitaria || false,
    cliente_vat: initialData?.cliente_vat || '',
    concepto: initialData?.concepto || '',
    lineas_factura: initialData?.lineas_factura || [{ descripcion: '', cantidad: 1, precio_unitario: 0, tipo_iva: '21' }],
    aplica_retencion_factura: initialData?.aplica_retencion_factura || false,
    porcentaje_retencion_factura: initialData?.porcentaje_retencion_factura || 15,
    fecha_factura: initialData?.fecha_factura || new Date().toISOString().split('T')[0],
    fecha_vencimiento: initialData?.fecha_vencimiento || '',
    metodo_pago: initialData?.metodo_pago || 'transferencia',
    observaciones: initialData?.observaciones || '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!activeTenant) return;
    api.get(`/catalog/clients?tenant_id=${activeTenant}`)
      .then((res) => setCatalogClients(res.data || []))
      .catch(() => setCatalogClients([]));
  }, [activeTenant]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.lineas_factura];
    newLines[index] = { ...newLines[index], [field]: value };
    setFormData(prev => ({ ...prev, lineas_factura: newLines }));
  };

  const addLine = () => {
    setFormData(prev => ({
      ...prev,
      lineas_factura: [...prev.lineas_factura, { descripcion: '', cantidad: 1, precio_unitario: 0, tipo_iva: '21' }]
    }));
  };

  const removeLine = (index) => {
    if (formData.lineas_factura.length > 1) {
      setFormData(prev => ({
        ...prev,
        lineas_factura: prev.lineas_factura.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateTotal = () => {
    let subtotal = 0;
    let totalIva = 0;
    
    formData.lineas_factura.forEach(line => {
      const lineTotal = (line.cantidad || 0) * (line.precio_unitario || 0);
      subtotal += lineTotal;
      totalIva += lineTotal * (parseFloat(line.tipo_iva) / 100);
    });
    
    let retencion = 0;
    if (formData.aplica_retencion_factura) {
      retencion = subtotal * (formData.porcentaje_retencion_factura / 100);
    }
    
    return {
      subtotal: subtotal.toFixed(2),
      totalIva: totalIva.toFixed(2),
      retencion: retencion.toFixed(2),
      total: (subtotal + totalIva - retencion).toFixed(2)
    };
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.cliente_nombre || formData.cliente_nombre.length < 3) {
      newErrors.cliente_nombre = 'Mínimo 3 caracteres';
    }
    if (!formData.cliente_nif || formData.cliente_nif.length < 8) {
      newErrors.cliente_nif = 'NIF/CIF requerido';
    }
    if (!formData.cliente_email || !/\S+@\S+\.\S+/.test(formData.cliente_email)) {
      newErrors.cliente_email = 'Email inválido';
    }
    if (!formData.cliente_direccion || formData.cliente_direccion.length < 10) {
      newErrors.cliente_direccion = 'Dirección completa requerida';
    }
    if (!formData.concepto || formData.concepto.length < 10) {
      newErrors.concepto = 'Mínimo 10 caracteres';
    }
    if (formData.operacion_intracomunitaria && !formData.cliente_vat) {
      newErrors.cliente_vat = 'VAT requerido para operaciones intracomunitarias';
    }
    if (formData.lineas_factura.some(l => !l.descripcion || l.cantidad <= 0 || l.precio_unitario <= 0)) {
      newErrors.lineas = 'Todas las líneas deben tener descripción, cantidad y precio';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleSelectClient = (clientId) => {
    setSelectedClientId(clientId);
    const client = catalogClients.find((item) => item.id === clientId);
    if (!client) return;
    handleChange('cliente_nombre', client.name || '');
    handleChange('cliente_nif', client.nif || '');
    handleChange('cliente_email', client.email || '');
    handleChange('cliente_direccion', client.address || '');
  };

  const totals = calculateTotal();
  const showIntracomunitario = formData.cliente_pais !== 'ES' && EU_COUNTRIES.includes(formData.cliente_pais);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Datos del Cliente */}
      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900 border-b pb-2">Datos del Cliente</h3>

        {catalogClients.length > 0 && (
          <div>
            <Label>Cliente registrado</Label>
            <Select value={selectedClientId} onValueChange={handleSelectClient}>
              <SelectTrigger data-testid="select-cliente-catalogo">
                <SelectValue placeholder="Selecciona un cliente" />
              </SelectTrigger>
              <SelectContent>
                {catalogClients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name} ({client.nif})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Nombre/Razón Social *</Label>
            <Input
              value={formData.cliente_nombre}
              onChange={(e) => handleChange('cliente_nombre', e.target.value)}
              placeholder="Nombre del cliente"
              className={errors.cliente_nombre ? 'border-red-500' : ''}
              data-testid="input-cliente-nombre"
            />
            {errors.cliente_nombre && <p className="text-red-500 text-xs mt-1">{errors.cliente_nombre}</p>}
          </div>
          
          <div>
            <Label>NIF/CIF *</Label>
            <Input
              value={formData.cliente_nif}
              onChange={(e) => handleChange('cliente_nif', e.target.value.toUpperCase())}
              placeholder="B12345678"
              className={errors.cliente_nif ? 'border-red-500' : ''}
              data-testid="input-cliente-nif"
            />
            {errors.cliente_nif && <p className="text-red-500 text-xs mt-1">{errors.cliente_nif}</p>}
          </div>
          
          <div>
            <Label>Email *</Label>
            <Input
              type="email"
              value={formData.cliente_email}
              onChange={(e) => handleChange('cliente_email', e.target.value)}
              placeholder="cliente@email.com"
              className={errors.cliente_email ? 'border-red-500' : ''}
              data-testid="input-cliente-email"
            />
            {errors.cliente_email && <p className="text-red-500 text-xs mt-1">{errors.cliente_email}</p>}
          </div>
          
          <div>
            <Label>País *</Label>
            <Select value={formData.cliente_pais} onValueChange={(v) => handleChange('cliente_pais', v)}>
              <SelectTrigger data-testid="select-cliente-pais">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAISES.map(p => (
                  <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="md:col-span-2">
            <Label>Dirección *</Label>
            <Textarea
              value={formData.cliente_direccion}
              onChange={(e) => handleChange('cliente_direccion', e.target.value)}
              placeholder="Dirección completa incluyendo código postal y ciudad"
              rows={2}
              className={errors.cliente_direccion ? 'border-red-500' : ''}
              data-testid="input-cliente-direccion"
            />
            {errors.cliente_direccion && <p className="text-red-500 text-xs mt-1">{errors.cliente_direccion}</p>}
          </div>
          
          {showIntracomunitario && (
            <>
              <div className="md:col-span-2 flex items-center space-x-2">
                <Checkbox
                  id="intracomunitaria"
                  checked={formData.operacion_intracomunitaria}
                  onCheckedChange={(checked) => handleChange('operacion_intracomunitaria', checked)}
                />
                <Label htmlFor="intracomunitaria" className="cursor-pointer">
                  Operación intracomunitaria (exenta de IVA)
                </Label>
              </div>
              
              {formData.operacion_intracomunitaria && (
                <div>
                  <Label>VAT del Cliente *</Label>
                  <Input
                    value={formData.cliente_vat}
                    onChange={(e) => handleChange('cliente_vat', e.target.value.toUpperCase())}
                    placeholder="DE123456789"
                    className={errors.cliente_vat ? 'border-red-500' : ''}
                  />
                  {errors.cliente_vat && <p className="text-red-500 text-xs mt-1">{errors.cliente_vat}</p>}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Concepto */}
      <div className="space-y-4">
        <h3 className="font-semibold text-slate-900 border-b pb-2">Concepto de la Factura</h3>
        <div>
          <Label>Descripción general *</Label>
          <Textarea
            value={formData.concepto}
            onChange={(e) => handleChange('concepto', e.target.value)}
            placeholder="Describe el servicio o producto facturado..."
            rows={3}
            className={errors.concepto ? 'border-red-500' : ''}
            data-testid="input-concepto"
          />
          {errors.concepto && <p className="text-red-500 text-xs mt-1">{errors.concepto}</p>}
        </div>
      </div>

      {/* Líneas de Factura */}
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b pb-2">
          <h3 className="font-semibold text-slate-900">Líneas de Factura</h3>
          <Button type="button" variant="outline" size="sm" onClick={addLine}>
            <Plus className="w-4 h-4 mr-1" /> Añadir línea
          </Button>
        </div>
        
        {errors.lineas && (
          <p className="text-red-500 text-sm flex items-center gap-1">
            <AlertCircle className="w-4 h-4" /> {errors.lineas}
          </p>
        )}
        
        <div className="space-y-3">
          {formData.lineas_factura.map((line, index) => (
            <div key={index} className="grid grid-cols-12 gap-2 items-end bg-slate-50 p-3 rounded-lg">
              <div className="col-span-12 md:col-span-5">
                <Label className="text-xs">Descripción</Label>
                <Input
                  value={line.descripcion}
                  onChange={(e) => handleLineChange(index, 'descripcion', e.target.value)}
                  placeholder="Descripción del producto/servicio"
                />
              </div>
              <div className="col-span-4 md:col-span-2">
                <Label className="text-xs">Cantidad</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={line.cantidad}
                  onChange={(e) => handleLineChange(index, 'cantidad', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="col-span-4 md:col-span-2">
                <Label className="text-xs">Precio (€)</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={line.precio_unitario}
                  onChange={(e) => handleLineChange(index, 'precio_unitario', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div className="col-span-3 md:col-span-2">
                <Label className="text-xs">IVA</Label>
                <Select value={line.tipo_iva} onValueChange={(v) => handleLineChange(index, 'tipo_iva', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_IVA.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeLine(index)}
                  disabled={formData.lineas_factura.length === 1}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Retención */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="retencion"
          checked={formData.aplica_retencion_factura}
          onCheckedChange={(checked) => handleChange('aplica_retencion_factura', checked)}
        />
        <Label htmlFor="retencion" className="cursor-pointer">
          Aplicar retención IRPF
        </Label>
        {formData.aplica_retencion_factura && (
          <Input
            type="number"
            min="0"
            max="100"
            value={formData.porcentaje_retencion_factura}
            onChange={(e) => handleChange('porcentaje_retencion_factura', parseFloat(e.target.value) || 0)}
            className="w-20"
          />
        )}
        {formData.aplica_retencion_factura && <span className="text-sm">%</span>}
      </div>

      {/* Totales */}
      <div className="bg-slate-100 rounded-lg p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span>Subtotal:</span>
          <span>{totals.subtotal} €</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>IVA:</span>
          <span>{totals.totalIva} €</span>
        </div>
        {formData.aplica_retencion_factura && (
          <div className="flex justify-between text-sm text-red-600">
            <span>Retención ({formData.porcentaje_retencion_factura}%):</span>
            <span>-{totals.retencion} €</span>
          </div>
        )}
        <div className="flex justify-between font-bold text-lg border-t pt-2">
          <span>Total:</span>
          <span>{totals.total} €</span>
        </div>
      </div>

      {/* Fechas y Método Pago */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label>Fecha Factura</Label>
          <Input
            type="date"
            value={formData.fecha_factura}
            onChange={(e) => handleChange('fecha_factura', e.target.value)}
          />
        </div>
        <div>
          <Label>Fecha Vencimiento</Label>
          <Input
            type="date"
            value={formData.fecha_vencimiento}
            onChange={(e) => handleChange('fecha_vencimiento', e.target.value)}
          />
        </div>
        <div>
          <Label>Método de Pago *</Label>
          <Select value={formData.metodo_pago} onValueChange={(v) => handleChange('metodo_pago', v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {METODOS_PAGO.map(m => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Observaciones */}
      <div>
        <Label>Observaciones (opcional)</Label>
        <Textarea
          value={formData.observaciones}
          onChange={(e) => handleChange('observaciones', e.target.value)}
          placeholder="Notas adicionales..."
          rows={2}
        />
      </div>

      <Button type="submit" className="w-full" data-testid="btn-submit-form">
        Continuar <ChevronRight className="w-4 h-4 ml-2" />
      </Button>
    </form>
  );
}
